import React, { useState } from 'react';
import api from '../../utils/api';
import Button from '../UI/Button';
import Input from '../UI/Input';

export default function MaterialUpload({ courseId, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    materialType: 'LINK',
    fileUrl: '',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadMode, setUploadMode] = useState('url'); // 'url' or 'file'
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [autoGenerate, setAutoGenerate] = useState(true); // 자동 생성 옵션
  const [generatingContent, setGeneratingContent] = useState(false);

  const materialTypes = [
    { value: 'PDF', label: 'PDF 문서', icon: '📄' },
    { value: 'TEXT', label: '텍스트', icon: '📝' },
    { value: 'LINK', label: '링크', icon: '🔗' },
    { value: 'VIDEO', label: '동영상', icon: '🎥' },
    { value: 'IMAGE', label: '이미지', icon: '🖼️' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (errors.file) {
        setErrors(prev => ({
          ...prev,
          file: null
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = '자료 제목을 입력해주세요.';
    }

    // LINK나 TEXT 타입은 항상 URL 필요
    if (formData.materialType === 'LINK' || formData.materialType === 'TEXT') {
      if (!formData.fileUrl.trim()) {
        newErrors.fileUrl = 'URL을 입력해주세요.';
      } else if (formData.materialType === 'LINK' && !isValidUrl(formData.fileUrl)) {
        newErrors.fileUrl = '올바른 URL 형식이 아닙니다.';
      }
    } else {
      // PDF, IMAGE, VIDEO 타입은 파일 또는 URL 중 하나 필요
      if (uploadMode === 'file' && !selectedFile) {
        newErrors.file = '파일을 선택해주세요.';
      } else if (uploadMode === 'url' && !formData.fileUrl.trim()) {
        newErrors.fileUrl = 'URL을 입력해주세요.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);
      let finalFileUrl = formData.fileUrl;

      // 파일 업로드 모드이고 파일이 선택된 경우
      if (uploadMode === 'file' && selectedFile) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', selectedFile);

        // 파일 업로드 API 호출
        const uploadResponse = await api.post(
          `/courses/${courseId}/materials/upload`,
          uploadFormData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        if (uploadResponse.data.success) {
          finalFileUrl = uploadResponse.data.file.url;
        } else {
          throw new Error('파일 업로드에 실패했습니다.');
        }
      }

      // 자료 메타데이터 저장
      const materialData = {
        title: formData.title,
        description: formData.description,
        type: formData.materialType,
        content: finalFileUrl,
      };

      const response = await api.post(`/courses/${courseId}/materials`, materialData);

      if (response.data.success) {
        const createdMaterial = response.data.material;

        // 자동 생성 옵션이 활성화된 경우 과제 및 퀴즈 생성
        if (autoGenerate) {
          try {
            setGeneratingContent(true);
            const generateResponse = await api.post(
              `/courses/${courseId}/materials/${createdMaterial.id}/generate`
            );

            if (generateResponse.data.success) {
              console.log('과제 및 퀴즈가 자동으로 생성되었습니다:', generateResponse.data);
            }
          } catch (genError) {
            console.error('자동 생성 오류:', genError);
            // 자동 생성 실패는 치명적 오류가 아니므로 계속 진행
          } finally {
            setGeneratingContent(false);
          }
        }

        onSuccess();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || '자료 업로드에 실패했습니다.';
      setErrors({ submit: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Input
        label="자료 제목"
        name="title"
        value={formData.title}
        onChange={handleChange}
        error={errors.title}
        placeholder="예: 1단원 교과서 자료"
        required
      />

      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          자료 유형
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {materialTypes.map((type) => (
            <button
              key={type.value}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, materialType: type.value }))}
              className={`p-3 rounded-xl border-2 transition-all ${
                formData.materialType === type.value
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
            >
              <div className="text-2xl mb-1">{type.icon}</div>
              <div className="text-sm font-medium text-neutral-700">{type.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* PDF, IMAGE, VIDEO 타입은 파일 업로드 또는 URL 선택 가능 */}
      {formData.materialType !== 'LINK' && formData.materialType !== 'TEXT' && (
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            업로드 방식
          </label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setUploadMode('file')}
              className={`flex-1 p-3 rounded-xl border-2 transition-all ${
                uploadMode === 'file'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
            >
              <div className="text-lg">📁</div>
              <div className="text-sm font-medium text-neutral-700">파일 업로드</div>
            </button>
            <button
              type="button"
              onClick={() => setUploadMode('url')}
              className={`flex-1 p-3 rounded-xl border-2 transition-all ${
                uploadMode === 'url'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-neutral-200 hover:border-neutral-300'
              }`}
            >
              <div className="text-lg">🔗</div>
              <div className="text-sm font-medium text-neutral-700">URL 입력</div>
            </button>
          </div>
        </div>
      )}

      {/* 파일 업로드 입력 */}
      {uploadMode === 'file' && formData.materialType !== 'LINK' && formData.materialType !== 'TEXT' && (
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            파일 선택
          </label>
          <input
            type="file"
            onChange={handleFileChange}
            accept={
              formData.materialType === 'PDF' ? '.pdf' :
              formData.materialType === 'IMAGE' ? 'image/*' :
              formData.materialType === 'VIDEO' ? 'video/*' : '*'
            }
            className="w-full px-4 py-2.5 rounded-xl border-2 border-neutral-200 focus:border-primary-500 focus:outline-none transition-colors duration-200"
          />
          {selectedFile && (
            <p className="mt-2 text-sm text-neutral-600">
              선택된 파일: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
            </p>
          )}
          {errors.file && (
            <p className="mt-1.5 text-sm text-danger-500">{errors.file}</p>
          )}
          <p className="mt-1.5 text-xs text-neutral-500">
            최대 파일 크기: 50MB
          </p>
        </div>
      )}

      {/* URL 입력 (LINK, TEXT는 항상 표시, 나머지는 uploadMode가 'url'일 때만) */}
      {(formData.materialType === 'LINK' || formData.materialType === 'TEXT' || uploadMode === 'url') && (
        <Input
          label={formData.materialType === 'LINK' ? '링크 URL' : '파일 URL'}
          name="fileUrl"
          value={formData.fileUrl}
          onChange={handleChange}
          error={errors.fileUrl}
          placeholder={
            formData.materialType === 'LINK'
              ? 'https://example.com'
              : 'https://example.com/file.pdf'
          }
          required
        />
      )}

      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          자료 설명 (선택사항)
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="w-full px-4 py-2.5 rounded-xl border-2 border-neutral-200 focus:border-primary-500 focus:outline-none transition-colors duration-200 min-h-[100px]"
          placeholder="자료에 대한 간단한 설명..."
        />
      </div>

      {/* 자동 생성 옵션 */}
      <div className="p-4 bg-primary-50 border-2 border-primary-200 rounded-xl">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={autoGenerate}
            onChange={(e) => setAutoGenerate(e.target.checked)}
            className="mt-1 w-5 h-5 text-primary-500 border-2 border-primary-300 rounded focus:ring-2 focus:ring-primary-500"
          />
          <div className="flex-1">
            <div className="font-medium text-neutral-800 mb-1">
              🤖 AI 자동 생성 (권장)
            </div>
            <p className="text-sm text-neutral-600">
              이 자료를 바탕으로 과제와 퀴즈를 AI가 자동으로 생성합니다.
              생성된 과제와 퀴즈는 나중에 수정할 수 있습니다.
            </p>
          </div>
        </label>
      </div>

      {errors.submit && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-xl">
          <p className="text-sm text-danger-600">{errors.submit}</p>
        </div>
      )}

      {generatingContent && (
        <div className="p-4 bg-info-50 border border-info-200 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-info-500"></div>
            <p className="text-sm text-info-700">
              AI가 과제와 퀴즈를 생성하고 있습니다... (약 10-20초 소요)
            </p>
          </div>
        </div>
      )}

      <div className="flex gap-3 pt-4">
        <Button
          type="submit"
          disabled={loading || generatingContent}
          className="flex-1"
        >
          {generatingContent
            ? 'AI 생성 중...'
            : loading
            ? '업로드 중...'
            : '업로드'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading || generatingContent}
        >
          취소
        </Button>
      </div>
    </form>
  );
}
